import {FC} from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {TextFieldElement} from 'react-hook-form-mui';
import {Box, Chip, MenuItem, Stack, Typography} from '@mui/material';

import {GeoFilterOption, useGetGeoFilterOptions} from '@entities/common';
import {
  imageFileTypes,
  ImageUploadField,
  ObjectSelect,
  RhfDateTimePicker,
  TranslateFieldButton,
} from '@shared/components';
import {resolveEnglishText} from '@shared/helpers';
import {TranslationDict} from '@shared/types';
import {dummyFunc} from '@shared/utils';
import {FormRow} from '@widgets/common';
import {openAddTranslationsDialog} from '@widgets/common/AddTranslationsDialog';
import {FormValues} from './v8nSchema';

interface Props {
  nameTranslations: TranslationDict;
  onNameTranslationsChange: (translations: TranslationDict) => void;
  existingLogoUrl?: string;
}

export const FormFields: FC<Props> = ({
  nameTranslations,
  onNameTranslationsChange,
  existingLogoUrl,
}) => {
  const {
    control,
    setValue,
    formState: {dirtyFields},
  } = useFormContext<FormValues>();
  const currentName = useWatch({control, name: 'name'});
  const {data: geoOptions = []} = useGetGeoFilterOptions();

  return (
    <Stack spacing={5} mt={2}>
      <Box display="flex" alignItems="center" gap={2}>
        <TextFieldElement
          name="name"
          label="Name"
          control={control}
          autoFocus
          sx={{flex: 1}}
        />
        <TranslateFieldButton
          onClick={() => {
            void openAddTranslationsDialog({
              fieldName: 'Name',
              initialTranslations: nameTranslations,
              enTrans: resolveEnglishText(
                nameTranslations,
                currentName,
                !!dirtyFields.name,
              ),
            })
              .then((translations: TranslationDict) => {
                setValue('name', translations.en ?? currentName);
                onNameTranslationsChange(translations);
                return;
              })
              .catch(dummyFunc);
          }}
        />
      </Box>

      <FormRow>
        <TextFieldElement
          name="appUrl"
          label="App URL"
          control={control}
          sx={{flex: 1}}
        />
        <TextFieldElement
          name="webUrl"
          label="Web URL"
          control={control}
          sx={{flex: 1}}
        />
      </FormRow>

      <TextFieldElement
        name="order"
        label="Order"
        control={control}
        type="number"
        sx={{maxWidth: 160}}
        slotProps={{htmlInput: {min: 0, step: 1}}}
      />

      <FormRow>
        <RhfDateTimePicker
          name="activeFrom"
          control={control}
          label="Active from"
        />
        <RhfDateTimePicker
          name="activeTo"
          control={control}
          label="Active to"
        />
      </FormRow>

      <Controller
        name="geos"
        control={control}
        render={({field}) => {
          const selectedIds = new Set((field.value ?? []).map(opt => opt.id));

          return (
            <ObjectSelect<GeoFilterOption>
              label="GEO tags"
              multiple
              options={geoOptions}
              labelProp="name"
              valueProp="id"
              value={geoOptions.filter(opt => selectedIds.has(opt.id))}
              onChange={val => field.onChange(val ?? [])}
              renderOption={opt => (
                <MenuItem key={opt.id} value={opt.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {opt.logo_url && (
                      <img
                        src={opt.logo_url}
                        alt=""
                        width="auto"
                        height={20}
                        style={{flexShrink: 0}}
                      />
                    )}
                    <Typography>{opt.name || opt.id}</Typography>
                  </Box>
                </MenuItem>
              )}
              renderValue={selected => (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {(selected as string[]).map(id => {
                    const opt = geoOptions.find(o => o.id === id);
                    return (
                      <Chip
                        key={id}
                        size="small"
                        label={opt ? opt.name || opt.id : id}
                      />
                    );
                  })}
                </Box>
              )}
            />
          );
        }}
      />

      <Stack spacing={1} width="100%">
        <ImageUploadField<FormValues>
          fieldName="logoFile"
          label="Banner image"
          acceptedFileTypes={imageFileTypes}
          existingFileUrl={existingLogoUrl}
          width="100%"
          height={200}
          imageFit="cover"
          alt="Banner"
        />
      </Stack>
    </Stack>
  );
};
